/**
 * The "expand" affordance for the instructions textarea: a near-fullscreen
 * dialog holding a markdown-highlighted CodeMirror, for prompts too large to
 * edit through a six-row box.
 *
 * SAVE WRITES TO THE FORM, NOT TO THE SERVER. The agent page already owns
 * persistence — a dirty diff and a sticky Save bar that says exactly what will
 * be PATCHed — and a second write path from inside a dialog would race it and
 * double the ways a save can fail. So Save here applies the buffer to the
 * draft and closes; the bar lights up with the change like any other edit, and
 * the footer says so rather than letting "Save" overclaim.
 *
 * CANCEL IS THE ONLY OTHER EXIT, and Escape/overlay-click mean Cancel. The
 * buffer is seeded when the dialog OPENS, not kept in sync while typing in the
 * textarea underneath — the two are alternative views of the same field, never
 * live at the same time.
 */
import { Suspense, lazy, useEffect, useState } from 'react'
import { CheckIcon, CopyIcon, Maximize2Icon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// The editor and its CodeMirror dependencies load when the dialog first opens.
// See the header of instructions-editor.tsx.
const InstructionsEditor = lazy(() => import('@/components/instructions-editor'))

export function InstructionsExpand({
  value,
  onSave,
  readOnly,
}: {
  /** The draft's current instructions — read at the moment the dialog opens. */
  value: string
  /** Applies the edited buffer to the form draft. Never called when readOnly. */
  onSave: (next: string) => void
  readOnly: boolean
}) {
  const [open, setOpen] = useState(false)
  const [buffer, setBuffer] = useState('')
  const [copied, setCopied] = useState(false)

  // Seed on open; discard on any close. `open` is the only trigger on purpose —
  // reseeding on `value` changes would clobber typing if the page re-rendered.
  useEffect(() => {
    if (open) {
      setBuffer(value)
      setCopied(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const copy = () => {
    navigator.clipboard
      ?.writeText(buffer)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {
        // Clipboard denied (permissions, non-secure context). The text is on
        // screen and selectable; a failed convenience needs no error banner.
      })
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 gap-1 px-2 text-xs text-muted-foreground"
        onClick={() => setOpen(true)}
      >
        <Maximize2Icon className="size-3" aria-hidden />
        Expand
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[92vh] w-[96vw] max-w-none flex-col gap-3 sm:max-w-none">
          <DialogHeader>
            <DialogTitle>Instructions</DialogTitle>
            <DialogDescription>
              {readOnly
                ? 'Read-only for your role.'
                : 'Markdown-highlighted. Save applies the text to the form; the page’s Save bar is what persists it.'}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-hidden rounded-md border">
            <Suspense
              fallback={
                <p className="loading p-4 text-sm text-muted-foreground">Loading the editor…</p>
              }
            >
              <InstructionsEditor
                value={buffer}
                onChange={setBuffer}
                dark={document.documentElement.classList.contains('dark')}
                readOnly={readOnly}
              />
            </Suspense>
          </div>

          <DialogFooter className="items-center gap-2">
            <span className="mr-auto text-xs text-muted-foreground">
              {buffer.length.toLocaleString('en-GB')} characters
            </span>
            <Button type="button" variant="outline" size="sm" onClick={copy}>
              {copied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            {!readOnly && (
              <Button
                type="button"
                size="sm"
                disabled={buffer === value}
                onClick={() => {
                  onSave(buffer)
                  setOpen(false)
                }}
              >
                Save
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
